import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers, type Action } from 'redux';
import sessionStateReducer, { clearSession } from '@/redux/slices/sessionStateSlice';
import brandReducer from '@/redux/slices/brandSlice';
import colorModeReducer from '@/redux/slices/colorModeSlice';
import onboardingVoiceReducer from '@/redux/slices/onboardingVoiceSlice';
import uiContextReducer from '@/redux/slices/uiContextSlice';
import calendarNavReducer from '@/redux/slices/calendarNavSlice';
import notificationsPanelReducer from '@/redux/slices/notificationsPanelSlice';
import { onboardingApi } from '@/redux/internal-services/onboardingApi';
import { emailTemplatesApi } from '@/redux/email-templates/emailTemplatesApi';
import { brandApi } from '@/redux/internal-services/brandApi';
import { geoApi } from '@/redux/internal-services/geoApi';
import { usersApi } from '@/redux/internal-services/usersApi';
import { platformAdminApi } from '@/redux/internal-services/platformAdminApi';
import { agentsApi } from '@/redux/internal-services/agentsApi';
import { agentApi } from '@/redux/internal-services/agentApi';
import { workspaceApi } from '@/redux/internal-services/workspaceApi';
import { notesApi } from '@/redux/internal-services/notesApi';
import { notificationsAdminApi } from '@/redux/internal-services/notificationsAdminApi';
import { notificationsApi } from '@/redux/internal-services/notificationsApi';
import { queueAdminApi } from '@/redux/internal-services/queueAdminApi';
import { schedulerEventsApi } from '@/redux/internal-services/schedulerEventsApi';
import { workflowApi } from '@/redux/internal-services/workflowApi';
import { workflowEngineApi } from '@/redux/internal-services/workflowEngineApi';

const sessionPersistConfig = {
  key: 'session',
  storage,
  whitelist: ['accessToken', 'refreshToken', 'isAuthenticated', 'userEmail', 'userName', 'userId', 'role', 'roles', 'permissions', 'tenantId', 'subscriptionStatus', 'userOnboardingCompleted', 'samWelcomeShown'],
};

const colorModePersistConfig = {
  key: 'colorMode',
  storage,
  whitelist: ['mode'],
};

const combinedReducer = combineReducers({
  sessionState: persistReducer(sessionPersistConfig, sessionStateReducer),
  brand: brandReducer,
  colorMode: persistReducer(colorModePersistConfig, colorModeReducer),
  onboardingVoice: onboardingVoiceReducer,
  uiContext: uiContextReducer, // situational awareness — intentionally NOT persisted
  calendarNav: calendarNavReducer, // calendar anchor + view — not persisted, resets on page load
  notificationsPanel: notificationsPanelReducer, // bell-toggle open flag — not persisted
  [onboardingApi.reducerPath]: onboardingApi.reducer,
  [emailTemplatesApi.reducerPath]: emailTemplatesApi.reducer,
  [brandApi.reducerPath]: brandApi.reducer,
  [geoApi.reducerPath]: geoApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [platformAdminApi.reducerPath]: platformAdminApi.reducer,
  [agentsApi.reducerPath]: agentsApi.reducer,
  [agentApi.reducerPath]: agentApi.reducer,
  [workspaceApi.reducerPath]: workspaceApi.reducer,
  [notesApi.reducerPath]: notesApi.reducer,
  [notificationsAdminApi.reducerPath]: notificationsAdminApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [queueAdminApi.reducerPath]: queueAdminApi.reducer,
  [schedulerEventsApi.reducerPath]: schedulerEventsApi.reducer,
  [workflowApi.reducerPath]: workflowApi.reducer,
  [workflowEngineApi.reducerPath]: workflowEngineApi.reducer,
});

// Every RTK Query cache slice — dropped on logout so a new user signing in on the SAME browser
// (SPA navigation, no full page reload) never sees the previous user's cached data: profile
// (theme_mode), tenant brand colors, tasks, etc. Non-API slices reset via their own reducers
// (sessionState + colorMode both handle clearSession; brand is recomputed by ThemeLoader).
const API_REDUCER_PATHS = [
  onboardingApi.reducerPath,
  emailTemplatesApi.reducerPath,
  brandApi.reducerPath,
  geoApi.reducerPath,
  usersApi.reducerPath,
  platformAdminApi.reducerPath,
  agentsApi.reducerPath,
  agentApi.reducerPath,
  workspaceApi.reducerPath,
  notesApi.reducerPath,
  notificationsAdminApi.reducerPath,
  notificationsApi.reducerPath,
  queueAdminApi.reducerPath,
  workflowApi.reducerPath,
  workflowEngineApi.reducerPath,
] as const;

type RootReducerState = ReturnType<typeof combinedReducer>;

const rootReducer = (state: RootReducerState | undefined, action: Action): RootReducerState => {
  if (state && action.type === clearSession.type) {
    const next = { ...state } as Record<string, unknown>;
    for (const path of API_REDUCER_PATHS) {
      next[path] = undefined; // combineReducers re-initialises each API slice from its initialState
    }
    return combinedReducer(next as RootReducerState, action);
  }
  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      onboardingApi.middleware,
      emailTemplatesApi.middleware,
      brandApi.middleware,
      geoApi.middleware,
      usersApi.middleware,
      platformAdminApi.middleware,
      agentsApi.middleware,
      agentApi.middleware,
      workspaceApi.middleware,
      notesApi.middleware,
      notificationsAdminApi.middleware,
      notificationsApi.middleware,
      queueAdminApi.middleware,
      schedulerEventsApi.middleware,
      workflowApi.middleware,
      workflowEngineApi.middleware,
    ),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = (
  dispatch: AppDispatch,
  getState: () => RootState,
) => ReturnType;
